/**
 * SPA session integration — thin wrapper over `@ando/core/auth` for use inside
 * `apps/client` (Vite + future TanStack Router).
 *
 * Architecture (v1-aligned):
 *   - The BFF (`apps/web`) sets the `user_info` cookie on its origin.
 *   - In single-host prod, the SPA is served behind the same Ingress so the
 *     cookie is in scope and `getSession()` returns the payload directly.
 *   - In local dev the SPA runs on :1420 and the BFF on :3000. The cookie is
 *     therefore NOT visible to the SPA via `document.cookie`; the SPA falls
 *     back to `getCurrentUser()` (hits the BFF with credentials: include).
 *
 * Two helpers exported here:
 *   - `readCachedSession()` — sync, reads the cookie. Suitable for TanStack
 *     Router `beforeLoad` guards when the cookie is in scope.
 *   - `loadSession()` — async, falls back to /auth/me when the cookie is not
 *     visible. Suitable for Vite-only dev mode and for the initial bootstrap.
 */

import { revokeDesktopSession } from "@ando/core/api";
import { type UserInfo, clearSession, getCurrentUser, getSession, logout } from "@ando/core/auth";
import { getEnv } from "@ando/core/env";

import { clearDesktopTokens, readRefreshToken } from "@/features/auth/desktop/tokenStore";

/**
 * Read the cached session from the `user_info` cookie. Sync, never fetches.
 * Returns `null` when the cookie is absent (e.g. cross-origin SPA + BFF
 * setup, or after `clearSession()`).
 */
export function readCachedSession(): UserInfo | null {
  return getSession();
}

/**
 * Resolve the session — cookie first, `/auth/me` fallback. Use this on app
 * bootstrap; downstream consumers should call `readCachedSession()`.
 */
export async function loadSession(): Promise<UserInfo | null> {
  const cached = getSession();
  if (cached) return cached;
  try {
    return await getCurrentUser();
  } catch {
    return null;
  }
}

/**
 * Redirect the user to the login page. The current location is preserved
 * in `?redirect=` so the login flow can bounce them back after success.
 *
 * May 27, 2026 — split web vs Tauri targets. On the desktop binary the
 * Tauri webview cannot be navigated to ``http://localhost:3000/login``
 * (the v1 BFF host) because the Next.js login bundle fails to load
 * within Tauri's restricted asset protocol — the page reaches the
 * webview as raw unstyled HTML ("Bentornato" + email field + Google /
 * Microsoft logos with no CSS, user-reported May 27). On desktop we
 * navigate to the in-app SPA route ``/login`` which renders the
 * ``DesktopLogin`` component (handoff to system browser, RFC 8252).
 */
export function redirectToLogin(reason?: "session_expired" | "session_required"): void {
  if (typeof window === "undefined") return;
  const env = getEnv();
  const currentPath = window.location.pathname + window.location.search;
  const params = new URLSearchParams();
  if (reason) params.set("error", reason);
  if (currentPath && currentPath !== "/") params.set("redirect", currentPath);
  const qs = params.toString();
  if (env.isTauri) {
    // Local SPA route. Reloading the same origin keeps the asset
    // protocol intact so the Vite bundle remains styled.
    window.location.href = `/login${qs ? `?${qs}` : ""}`;
    return;
  }
  const webBase = env.webUrl || "";
  window.location.href = `${webBase}/login${qs ? `?${qs}` : ""}`;
}

/**
 * Log out — clear the BFF + local cookie, then redirect to login. Mirrors v1
 * SessionContext.logout (POST /auth/logout + clear user_info + reload).
 *
 * HANG-PROOF (Aug 27, 2026): the BE round-trip is raced against a 4s
 * ceiling — a slow or hanging ``POST /auth/logout`` must never trap the user
 * on a frozen screen (observed live: "il solito loader… che è lento").
 * ``logout()`` is already fail-open on ERRORS; this adds fail-open on TIME.
 * ``clearSession()`` is idempotent, so calling it again on the timeout path
 * guarantees the local teardown either way.
 */
export async function logoutAndRedirect(): Promise<void> {
  await Promise.race([
    getEnv().isTauri ? desktopLogout() : logout(),
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, 4000);
    }),
  ]);
  clearSession();
  if (getEnv().isTauri) {
    // Belt and braces: ``desktopLogout`` already wipes the vault, but if
    // it lost the 4s race the keychain must STILL be empty before the
    // reload below, or ``/login`` re-hydrates the old bundle and bounces
    // straight back into the workspace (Sep 4, 2026 — "sign-out rientra
    // subito"). ``clearDesktopTokens`` is idempotent.
    await clearDesktopTokens();
  }
  redirectToLogin();
}

/**
 * Desktop sign-out — the Bearer-only Tauri shell has no BFF cookie session
 * for ``/auth/logout`` to tear down, so:
 *   1. ask the BE to revoke the OIDC refresh token at Zitadel (RFC 7009,
 *      best-effort — a dead network must never trap the user signed in);
 *   2. wipe the OS-keychain bundle + in-memory cache (the part that keeps
 *      the session alive across reloads — see ``hydrateSession``).
 */
async function desktopLogout(): Promise<void> {
  try {
    const refreshToken = await readRefreshToken();
    await revokeDesktopSession({ refresh_token: refreshToken });
  } catch (error) {
    console.warn("[auth/session] desktop token revocation failed:", error);
  } finally {
    await clearDesktopTokens();
  }
}
